import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RiMailSendLine } from '@remixicon/react'
import type { EmailNotificationSetting, EmailNotificationSettingsResponse } from 'shared'
import {
  Alert,
  Button,
  Card,
  Field,
  Input,
  PageHeader,
  SectionSpinner,
  Switch,
  Textarea,
} from '../../components/ui'
import { api } from '../../utils/api'
import { toErrorMessage } from '../../lib/errors'

const BASE = '/api/admin/notification-settings'

/**
 * Cosa parte per email, e con che parole.
 *
 * Una riga per tipo, con l'interruttore accanto al testo che quel tipo manda:
 * la domanda "cosa riceve davvero la gente" si risponde guardando, senza
 * aprire altro.
 */
export default function NotificationSettings() {
  const { t } = useTranslation()

  const [settings, setSettings] = useState<EmailNotificationSetting[]>([])
  const [placeholders, setPlaceholders] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [openType, setOpenType] = useState<string | null>(null)
  const [saving, setSaving] = useState<string | null>(null)

  const load = useCallback(async () => {
    const response = await api.get<EmailNotificationSettingsResponse>(BASE)
    setSettings(response.data)
    setPlaceholders(response.placeholders)
  }, [])

  useEffect(() => {
    load()
      .catch((err) => setError(toErrorMessage(err) ?? 'error'))
      .finally(() => setLoading(false))
  }, [load])

  const save = async (
    setting: EmailNotificationSetting,
    changes: Partial<Pick<EmailNotificationSetting, 'enabled' | 'subject' | 'body'>>
  ) => {
    const next = { ...setting, ...changes }
    setError(null)
    setSaving(setting.type)

    // Ottimistico: l'interruttore deve rispondere subito, e un errore lo
    // rimette com'era invece di lasciarlo a metà.
    setSettings((current) => current.map((row) => (row.type === next.type ? next : row)))

    try {
      await api.put(`${BASE}/${next.type}`, {
        enabled: next.enabled,
        subject: next.subject,
        body: next.body,
      })
    } catch (err) {
      setSettings((current) => current.map((row) => (row.type === setting.type ? setting : row)))
      setError(toErrorMessage(err) ?? 'error')
    } finally {
      setSaving(null)
    }
  }

  if (loading) return <SectionSpinner />

  return (
    <div>
      <PageHeader
        title={t('notificationSettings.title')}
        subtitle={t('notificationSettings.subtitle')}
      />

      {error && (
        <Alert variant="error" className="mb-6">
          {error}
        </Alert>
      )}

      <Alert variant="info" className="mb-6">
        {t('notificationSettings.channelNote')}
      </Alert>

      <Card className="divide-y divide-gray-100 dark:divide-gray-800">
        {settings.map((setting) => {
          const open = openType === setting.type

          return (
            <div key={setting.type} className="p-4">
              <div className="flex items-start gap-4">
                <div className="min-w-0 flex-1">
                  <p className="font-medium text-gray-950 dark:text-white">{setting.label}</p>
                  <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                    {setting.audience}
                  </p>
                </div>
                <Switch
                  checked={setting.enabled}
                  disabled={saving === setting.type}
                  onCheckedChange={(enabled) => save(setting, { enabled })}
                  aria-label={setting.label}
                />
              </div>

              {/* Il testo si apre solo per il tipo su cui si sta lavorando:
                  undici caselle di testo aperte insieme non si leggono. */}
              {setting.enabled && (
                <div className="mt-3">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setOpenType(open ? null : setting.type)}
                  >
                    <RiMailSendLine className="size-4" aria-hidden="true" />
                    {open ? t('notificationSettings.hideText') : t('notificationSettings.editText')}
                  </Button>
                </div>
              )}

              {setting.enabled && open && (
                <div className="mt-4 space-y-4">
                  <Field label={t('notificationSettings.subject')}>
                    <Input
                      value={setting.subject ?? ''}
                      placeholder={setting.default_subject}
                      onChange={(event) =>
                        setSettings((current) =>
                          current.map((row) =>
                            row.type === setting.type
                              ? { ...row, subject: event.target.value }
                              : row
                          )
                        )
                      }
                    />
                  </Field>

                  <Field
                    label={t('notificationSettings.body')}
                    hint={t('notificationSettings.placeholders', {
                      list: placeholders.map((name) => `{{${name}}}`).join(' '),
                    })}
                  >
                    <Textarea
                      rows={5}
                      value={setting.body ?? ''}
                      placeholder={setting.default_body}
                      onChange={(event) =>
                        setSettings((current) =>
                          current.map((row) =>
                            row.type === setting.type ? { ...row, body: event.target.value } : row
                          )
                        )
                      }
                    />
                  </Field>

                  <div className="flex items-center gap-3">
                    <Button
                      type="button"
                      loading={saving === setting.type}
                      onClick={() => save(setting, {})}
                    >
                      {t('common.save')}
                    </Button>
                    {/* Svuotare i campi è "ripristina": il server legge il
                        vuoto come "torna al testo di sistema". */}
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={() => save(setting, { subject: null, body: null })}
                    >
                      {t('notificationSettings.restore')}
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )
        })}
      </Card>
    </div>
  )
}
